import { Bank, CurrencyEur, PencilSimple, Percent, PiggyBank } from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand } from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { kaufKennzahlen } from '../../../../../lib/immobilien/einheitDetail';
import { useEinheitSeite } from '../EinheitSeitenKontext';
import { DatenZeile, KartenRaster } from './teile';

const zahl = (v: unknown): number => { const x = Number(String(v ?? '').replace(',', '.')); return Number.isFinite(x) ? x : 0; };
const betragOderLeer = (v: unknown) => (zahl(v) > 0 ? eur(zahl(v)) : '');
const prozent = (v: unknown) => (zahl(v) > 0 ? `${zahl(v).toLocaleString('de-DE')} %` : '');

/** „Kauf & Finanzierung“: Felder aus dem Einheiten-Formular (Kaufpreis, Nebenkosten, Darlehen). */
export function KaufBereich() {
  const s = useEinheitSeite();
  const e = s.daten.einheit;
  const k = kaufKennzahlen(e, s.miete.kalt);
  if (!k.hatDaten) {
    return (
      <LeerZustand icon={Bank} titel="Keine Kaufdaten" text="Kaufpreis, Kaufnebenkosten und Finanzierung werden im Einheiten-Formular erfasst.">
        <ImmoButton art="akzent" icon={PencilSimple} onClick={() => s.bearbeiteEinheit('kauf')}>Kaufdaten erfassen</ImmoButton>
      </LeerZustand>
    );
  }
  return (
    <>
      <KpiReihe>
        <KpiTile label="Gesamtinvestition" wert={eur(k.investition, { nachkomma: 0 })} sub={`Kaufpreis ${eur(k.kaufpreis, { nachkomma: 0 })} + NK ${eur(k.nebenkosten, { nachkomma: 0 })}`} icon={CurrencyEur} ton="acc" />
        <KpiTile label="Eigenkapital" wert={eur(k.eigenkapital, { nachkomma: 0 })} sub={k.darlehen > 0 ? `Darlehen ${eur(k.darlehen, { nachkomma: 0 })}` : 'ohne Darlehen'} icon={PiggyBank} />
        <KpiTile label="Bruttorendite" wert={k.bruttorendite !== null ? `${k.bruttorendite.toLocaleString('de-DE')} %` : '–'} sub={k.bruttorendite !== null ? 'Kaltmiete × 12 / Kaufpreis' : s.aktuell ? 'Kaufpreis fehlt' : 'Leerstand'} icon={Percent} />
      </KpiReihe>
      <KartenRaster>
        <InnenKarte>
          <KartenTitel>Kauf</KartenTitel>
          <div className="flex flex-col">
            <DatenZeile label="Objektkaufpreis" wert={betragOderLeer(e.kaufpreis)} zahl />
            {zahl(e.kaufpreis_stellplatz) > 0 && <DatenZeile label="Stellplatz" wert={betragOderLeer(e.kaufpreis_stellplatz)} zahl />}
            {zahl(e.kaufpreis_garage) > 0 && <DatenZeile label="Garage" wert={betragOderLeer(e.kaufpreis_garage)} zahl />}
            {typeof e.besitzuebergang === 'string' && e.besitzuebergang && <DatenZeile label="Besitzübergang" wert={deDatum(e.besitzuebergang)} zahl />}
            <DatenZeile label="Maklerkosten" wert={betragOderLeer(e.nk_makler)} zahl />
            <DatenZeile label="Notarkosten" wert={betragOderLeer(e.nk_notar)} zahl />
            <DatenZeile label="Anwaltskosten" wert={betragOderLeer(e.nk_anwalt)} zahl />
            <DatenZeile label="Grunderwerbsteuer" wert={betragOderLeer(e.nk_grunderwerbsteuer)} zahl />
            <DatenZeile label="Eintragungskosten" wert={betragOderLeer(e.nk_eintragung)} zahl />
            <DatenZeile label="Gutachter" wert={betragOderLeer(e.nk_gutachter)} zahl />
          </div>
        </InnenKarte>
        <InnenKarte>
          <KartenTitel>Finanzierung</KartenTitel>
          <div className="flex flex-col">
            <DatenZeile label="Bank" wert={k.bank} />
            {typeof e.finanz_ansprechpartner === 'string' && e.finanz_ansprechpartner && <DatenZeile label="Ansprechpartner" wert={e.finanz_ansprechpartner} />}
            <DatenZeile label="Darlehenssumme" wert={betragOderLeer(e.finanz_darlehenssumme)} zahl />
            <DatenZeile label="Zinssatz" wert={prozent(e.finanz_zinssatz)} zahl />
            <DatenZeile label="Anfängl. Tilgung" wert={prozent(e.finanz_tilgung)} zahl />
            <DatenZeile label="Annuität" wert={prozent(e.finanz_annuitaet)} zahl />
            <DatenZeile label="Rate / Monat" wert={k.rateMonat !== null ? eur(k.rateMonat) : ''} zahl />
            <DatenZeile label="Zinsbindungsfrist" wert={typeof e.finanz_zinsbindung === 'string' && e.finanz_zinsbindung ? deDatum(e.finanz_zinsbindung) : ''} zahl />
          </div>
        </InnenKarte>
      </KartenRaster>
      <ImmoButton icon={PencilSimple} onClick={() => s.bearbeiteEinheit('kauf')} style={{ alignSelf: 'flex-start' }}>Im Einheiten-Formular ändern</ImmoButton>
    </>
  );
}
